import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import { useQueryClient } from '@tanstack/react-query';
import {
  useListVectorStores,
  useCreateVectorStore,
  useListFiles,
  useDeleteFile,
  useReindexFile,
  useAddTextEntry,
  getListVectorStoresQueryKey,
  getListFilesQueryKey,
} from '../../api/generated/generated';
import type { VectorStoreResponse as VectorStore } from '../../api/generated';
import { getErrorMessage } from '../../api/client';
import { useSnackbar } from '../common/SnackbarProvider';
import { useUploadFile } from '../../hooks/useVectorStores';
import { FileUpload } from '../documents/FileUpload';
import { FileList } from '../documents/FileList';
import { useAuth } from '../../contexts/AuthContext';

const PERSONAL_KB_NAME = 'My Knowledge';

export function PersonalKnowledgeSection() {
  const { user } = useAuth();
  const uid = user?.uid ?? '';
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const storesQuery = useListVectorStores();
  const stores: VectorStore[] =
    storesQuery.data?.status === 200 ? storesQuery.data.data : [];
  const personalKb = stores.find(
    (s) => s.scope === 'individual' && s.scope_owner_id === uid,
  );

  const createStore = useCreateVectorStore({
    mutation: {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: getListVectorStoresQueryKey() }),
    },
  });

  const ensureKb = async (): Promise<string> => {
    if (personalKb) return personalKb.id;
    const res = await createStore.mutateAsync({
      data: { name: PERSONAL_KB_NAME, scope: 'individual', scope_owner_id: uid },
    });
    if (res.status !== 201) throw new Error('Failed to create personal knowledge base');
    return res.data.id;
  };

  if (storesQuery.isLoading) {
    return (
      <Paper sx={{ p: 3, mt: 3 }}>
        <CircularProgress size={20} />
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Personal Knowledge
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: 2
        }}>
        Reference material only your coaching draws on — upload files or paste notes. It stays
        private to you.
      </Typography>
      {personalKb ? (
        <PersonalKbContents storeId={personalKb.id} />
      ) : (
        <NewKbActions ensureKb={ensureKb} showSnackbar={showSnackbar} />
      )}
    </Paper>
  );
}

function NewKbActions({
  ensureKb,
  showSnackbar,
}: {
  ensureKb: () => Promise<string>;
  showSnackbar: (m: string, s?: 'error') => void;
}) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  // First action lazily creates the KB, then the contents view takes over on refetch.
  const handleCreate = async () => {
    setBusy(true);
    try {
      await ensureKb();
    } catch (err) {
      showSnackbar(getErrorMessage(err), 'error');
    } finally {
      queryClient.invalidateQueries({ queryKey: getListVectorStoresQueryKey() });
      setBusy(false);
    }
  };

  return (
    <Button variant="outlined" onClick={handleCreate} disabled={busy}>
      {busy ? 'Creating…' : 'Create my knowledge base'}
    </Button>
  );
}

function PersonalKbContents({ storeId }: { storeId: string }) {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const filesQuery = useListFiles(storeId, {
    query: {
      refetchInterval: (query) => {
        const resp = query.state.data;
        if (resp?.status !== 200) return false;
        return resp.data.some((f) => f.status === 'pending' || f.status === 'processing')
          ? 4000
          : false;
      },
    },
  });
  const files = filesQuery.data?.status === 200 ? filesQuery.data.data : [];

  const invalidateFiles = () =>
    queryClient.invalidateQueries({ queryKey: getListFilesQueryKey(storeId) });

  const uploadFile = useUploadFile(storeId);
  const deleteFile = useDeleteFile({ mutation: { onSuccess: invalidateFiles } });
  const reindexFile = useReindexFile({ mutation: { onSuccess: invalidateFiles } });
  const addText = useAddTextEntry({ mutation: { onSuccess: invalidateFiles } });

  const [textOpen, setTextOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');

  const handleAddText = async () => {
    try {
      await addText.mutateAsync({ storeId, data: { title: title.trim(), content: content.trim() } });
      showSnackbar('Note added');
      setTextOpen(false);
      setTitle('');
      setContent('');
    } catch (err) {
      showSnackbar(getErrorMessage(err), 'error');
    }
  };

  return (
    <Box>
      <Stack
        direction="row"
        spacing={2}
        sx={{
          alignItems: "center",
          mb: 2
        }}>
        <Box sx={{ flexGrow: 1 }}>
          <FileUpload
            onUpload={(file) => uploadFile.mutate(file, { onSuccess: invalidateFiles })}
            isUploading={uploadFile.isPending}
          />
        </Box>
        <Button startIcon={<NoteAddIcon />} variant="outlined" onClick={() => setTextOpen(true)}>
          Add text
        </Button>
      </Stack>
      {uploadFile.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Upload failed. Please try again.
        </Alert>
      )}
      {filesQuery.isLoading ? (
        <CircularProgress size={20} />
      ) : files.length === 0 ? (
        <Typography variant="body2" sx={{
          color: "text.secondary"
        }}>
          Nothing here yet.
        </Typography>
      ) : (
        <FileList
          files={files}
          onDelete={(fileId) => deleteFile.mutate({ storeId, fileId })}
          onReindex={(fileId) => reindexFile.mutate({ storeId, fileId })}
        />
      )}
      <Dialog open={textOpen} onClose={() => setTextOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add text</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
            slotProps={{
              htmlInput: { maxLength: 200 }
            }}
          />
          <TextField
            fullWidth
            label="Content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            multiline
            minRows={5}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTextOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddText}
            disabled={!title.trim() || !content.trim() || addText.isPending}
          >
            {addText.isPending ? 'Adding…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
